/**
 * DRAWING BETWEEN TWO STATES THE RENDERER HAS READ.
 *
 * -- THE NOTE THIS ANSWERS -----------------------------------------------------
 *
 * Owner, on the first watchable build (CONTRACT 1.5g note 5): "The boat seems to teleport
 * around rather than smoothly sail to its destination." Round 2 declined to interpolate on
 * the reasoning that the renderer only shows what `view()` reports. That was right about
 * the invariant and wrong about the player.
 *
 * -- WHAT WAS ACTUALLY MEASURED, AND WHY THE FILED NUMBERS WERE WRONG ----------
 *
 * The round-7 finding says the sim ticks at 1000 ms so the boat moves about 2.8 px once a
 * second. Both halves are false, measured against a real session (seed 4242, memory
 * storage, sampled every 1000 ms over ten minutes at two ages):
 *
 *            step interval (ms)          step size (art px)
 *   hour 1   min 5000 med 7000 max 21000   med 158  p90 169
 *   hour 6   min 2000 med 11000 max 22000  med 313  p90 317
 *
 * The position does not change on the sim tick. It changes when a cast lands XP, because
 * position is a function of progression, and a cast is 4 to 24 ticks. So the boat stands
 * still for seven to eleven seconds and then moves a third of a screen width. That is the
 * teleport, and it is fifty times larger than the figure on file.
 *
 * IT MATTERS FOR THE FIX. Bracketing with `view(t)` and `view(t + TICK_MS)` - the shape the
 * note proposed - would leave it untouched: both samples fall inside the same still stretch
 * about nine times in ten, and when they do straddle a step the whole 300 px jump happens
 * inside one second instead of instantly. The window has to bracket the STEP, not the tick.
 *
 * -- THE RULE, AND WHAT IT COSTS ----------------------------------------------
 *
 * At wall-clock `t` the renderer reads the two states either side of the window `t` falls
 * in and draws the point between them at the real fraction:
 *
 *   t0 = epoch + floor((t - epoch) / W) * W        t1 = t0 + W       f = (t - t0) / W
 *
 * Anchored to the SAVE'S OWN EPOCH, because that is the grid `view` itself quantises on;
 * anchored to absolute milliseconds the two samples could land inside one sim tick and the
 * motion would stall and lurch again.
 *
 * Three properties follow, and interpolate.test.ts asserts all three:
 *   1. THE DRAWN POSITION IS A PURE FUNCTION OF `t`. It never reads a clock, never
 *      accumulates and never sees a previous frame, so a tab drawing at 120 Hz and a tab
 *      drawing at 4 Hz land on the identical position for the identical instant. That is
 *      the fence the note asked for.
 *   2. IT INVENTS NO STATE. Both endpoints come out of `view`, which is a pure function of
 *      a timestamp and advances nothing. At every window boundary the drawn position is
 *      EXACTLY a state the simulation reports.
 *   3. IT DOES NOT ADVANCE THE SIMULATION. Nothing in this file calls `tick`.
 *
 * The cost, stated rather than discovered later: between boundaries the drawn position is
 * the chord of a step function, so it can be up to half a step - about 150 art px - from
 * where `view` says the boat is at that instant. That is inherent to any smoothing of a
 * step, it is bounded, and it is the trade the note asked for.
 */

import type { Px } from './scale';
import { WATER_FRAME_MS, waterTickAt } from './water';

/**
 * THE WINDOW. MEASURED: the position's own change interval is 5-22 ms x 1000 with a median
 * of 7-11 s, so a 10 s window contains no step about half the time and the boat would still
 * stand and lurch. 30 s contains three steps at the measured median, which is what makes the
 * motion continuous. It is also 30 sim ticks, so a window boundary is always a tick boundary.
 */
export const SMOOTH_WINDOW_MS = 30_000;

export interface Fix {
  readonly position: { readonly x: number; readonly y: number };
  readonly headingDeg: number;
  /**
   * HOW FAR INTO AN ARRIVAL THE BOAT IS at this state: 0 on the spiral, 1 made fast, the
   * game's own `callHoldWeight`.
   *
   * IT WAS OPTIONAL FOR ONE ROUND AND THAT IS EXACTLY WHAT WENT WRONG. It was left optional
   * so that readers written before arrivals existed would still compile, and the default was
   * 0, which is the camera this renderer had before arrivals existed. That is a silent
   * fallback to the old behaviour, and this file has no way to tell a reader that MEANS zero
   * from one that FORGOT. MEASURED at 2ce4e58: dropping `...arrivalAt(f)` from the boot
   * smoother in `world-entry.ts` - so a fresh page load gets the old camera and a reload after
   * any verb gets the new one - passed tsc, 224 render and app tests, both surfaces
   * instruments and all 16 browser fences. Required, that same plant is a compile error, and
   * a reader that genuinely has no arrival now has to say `hold: 0` out loud.
   *
   * The cost is four words in three test readers, which is the whole of what optionality was
   * buying. It closes the plant rather than the class - `{ ...arrivalAt(f), hold: 0 }` still
   * compiles - and the class is closed in `tools/lanes/platform-trust/wired.test.ts`.
   *
   * It is carried HERE, through the same window as the position, for a reason that was
   * MEASURED rather than reasoned. The camera's first shape took its weight from the drawn
   * distance to the mooring, and a screenshot at Cloven Rigg caught it: the fix said "made
   * fast", the DRAWN boat was 6.73 leagues off the place point because the smoother chords a
   * step function, so the weight came out 0.067 and the frame moved 9 px of the 134 the island
   * needed. The state and the picture disagreed, and the picture was the one on screen. A
   * weight read off the state and smoothed the same way the position is cannot do that: at
   * every window boundary it is EXACTLY the weight the simulation reports, which is the same
   * promise property 2 above makes about the position.
   */
  readonly hold: number;
  /**
   * WHICH place that weight belongs to, or null. Carried beside it because a weight without a
   * place cannot say what to look at, and the obvious substitute - the nearest charted place -
   * is wrong in a way the instrument caught: standing out of place-15 the boat is already nearer
   * place-16's point, and the frame flipped 123 px in a quarter of a league between two islands.
   * The route knows which place it is calling at; nothing has to guess.
   */
  readonly holdPlaceId: string | null;
  /**
   * WHEN THIS STATE'S MOORING BEGAN ON THE PICTURE, in save-clock milliseconds, or null while
   * the state is still on the run-in or calling nowhere. It is the time base of the pan below
   * (CONTRACT 1.5q(b)) and it is REQUIRED for the reason `hold` is: a reader that means "no
   * mooring" says null out loud.
   *
   * The session records no such instant - `VoyageFix.mooredAt` is a step with no timestamp -
   * so the host DERIVES it from the save's own clock, and a mooring has two kinds on this branch.
   * An EXPLICIT one (`fix.phase === 'moored'`, a SailTo order carried to its end) began at the
   * order's own `issuedAtMs + durationMs`, which the save persists. A NATURAL one (a call on the
   * drift) began when the track crossed the call's hold: `view(t)` is pure in `t` and
   * `trackDistance` is monotone in it, so that instant is found by bisection and is the same on
   * every reload; a Return order that ended inside the hold moves it to the return's own end.
   * Either is then snapped UP to the smoother boundary that contains it, because that boundary is
   * the first instant the DRAWN weight is 1: the chord below reaches `toHold` only at `t1`.
   * Snapped to the raw instant, the anchor dwell would be spent under the arrival's own ramp.
   */
  readonly moorEpochMs: number | null;
}

export interface Smoothed {
  /** The interpolated position, in the same units the reader reports. */
  readonly position: Px;
  /** The interpolated heading, shortest arc, wrapped to [0, 360). */
  readonly headingDeg: number;
  /**
   * The interpolated arrival weight, 0 to 1. Straight-line, because the ramp inside
   * `callHoldWeight` is already a smoothstep and easing an ease flattens the middle of it.
   *
   * IT IS PUBLISHED BUT THE CAMERA DOES NOT USE IT, and the reason is the same one that put
   * `from` and `to` on this struct. A single blended weight has lost which island it belongs to,
   * and across a window that spans two calls the two answers are different islands. The camera
   * therefore reads `fromHold`/`fromPlace` and `toHold`/`toPlace`, works out a shift for each,
   * and chords THOSE at `f` - which is what the position does, one level down.
   */
  readonly hold: number;
  /** The window this instant fell in, for the caller's own caching. */
  readonly t0: number;
  readonly t1: number;
  readonly f: number;
  /**
   * The two states the interpolation was drawn between, published rather than recomputed.
   * The host needs the window's velocity to decide how long it may sleep, and asking for it
   * by calling `smoothAt(t + 1000)` would evict the memo on every step.
   */
  readonly from: { readonly x: number; readonly y: number };
  readonly to: { readonly x: number; readonly y: number };
  /** The two arrival weights the interpolation was drawn between, and the places they name. */
  readonly fromHold: number;
  readonly toHold: number;
  readonly fromPlace: string | null;
  readonly toPlace: string | null;
  /** The two mooring epochs, one per endpoint, for the pan's time base. */
  readonly fromMoorEpochMs: number | null;
  readonly toMoorEpochMs: number | null;
}

/**
 * Shortest-arc interpolation on the circle, so 359 to 1 goes forward two degrees.
 *
 * The in-range early return is not an optimisation. `((x % 360) + 360) % 360` is NOT the
 * identity on a double already in [0, 360): adding 360 rounds off the low bit, and at a
 * window boundary that made the smoothed heading differ from the session's own in the last
 * place. The fence caught it, and a renderer that is nearly equal to the state it read is
 * not equal to it.
 */
export function lerpDeg(a: number, b: number, f: number): number {
  const d = (((b - a) % 360) + 540) % 360 - 180;
  const raw = a + d * f;
  return raw >= 0 && raw < 360 ? raw : ((raw % 360) + 360) % 360;
}

/**
 * The one function. `read` must be pure in its argument - `session.view(t).fix` is - and is
 * called at most twice, at the two window boundaries.
 */
export function smoothAt(read: (t: number) => Fix, epochMs: number, t: number, windowMs = SMOOTH_WINDOW_MS): Smoothed {
  const w = Math.max(1, windowMs);
  const t0 = epochMs + Math.floor((t - epochMs) / w) * w;
  const t1 = t0 + w;
  const f = (t - t0) / w;
  const a = read(t0);
  const b = read(t1);
  const ah = a.hold ?? 0;
  const bh = b.hold ?? 0;
  return {
    position: {
      x: a.position.x + (b.position.x - a.position.x) * f,
      y: a.position.y + (b.position.y - a.position.y) * f,
    },
    headingDeg: lerpDeg(a.headingDeg, b.headingDeg, f),
    hold: ah + (bh - ah) * f,
    t0,
    t1,
    f,
    from: a.position,
    to: b.position,
    fromHold: ah,
    toHold: bh,
    fromPlace: a.holdPlaceId,
    toPlace: b.holdPlaceId,
    fromMoorEpochMs: a.moorEpochMs,
    toMoorEpochMs: b.moorEpochMs,
  };
}

/**
 * `smoothAt` with the two endpoint reads memoised on the window.
 *
 * The memo is a CACHE AND NOTHING ELSE: it is keyed on `t0` alone, and a hit returns the
 * value a cold call would have returned. Drawing more often costs fewer reads and changes
 * no pixel, which is the property interpolate.test.ts checks by running the same instants
 * through a warm cache and a cold one and comparing.
 */
export function makeSmoother(read: (t: number) => Fix, epochMs: number, windowMs = SMOOTH_WINDOW_MS): {
  (t: number): Smoothed;
  reads: () => number;
} {
  let key = Number.NaN;
  let a: Fix | null = null;
  let b: Fix | null = null;
  let reads = 0;
  const counted = (t: number): Fix => {
    reads++;
    return read(t);
  };
  const fn = (t: number): Smoothed => {
    const w = Math.max(1, windowMs);
    const t0 = epochMs + Math.floor((t - epochMs) / w) * w;
    if (t0 !== key || a === null || b === null) {
      key = t0;
      a = counted(t0);
      b = counted(t0 + w);
    }
    const f = (t - t0) / w;
    const av = a;
    const bv = b;
    const ah = av.hold ?? 0;
    const bh = bv.hold ?? 0;
    return {
      position: {
        x: av.position.x + (bv.position.x - av.position.x) * f,
        y: av.position.y + (bv.position.y - av.position.y) * f,
      },
      headingDeg: lerpDeg(av.headingDeg, bv.headingDeg, f),
      hold: ah + (bh - ah) * f,
      t0,
      t1: t0 + w,
      f,
      from: av.position,
      to: bv.position,
      fromHold: ah,
      toHold: bh,
      fromPlace: av.holdPlaceId ?? null,
      toPlace: bv.holdPlaceId ?? null,
      fromMoorEpochMs: av.moorEpochMs ?? null,
      toMoorEpochMs: bv.moorEpochMs ?? null,
    };
  };
  fn.reads = (): number => reads;
  return fn;
}

// =============================================================================================
// THE FRAME ON ARRIVAL
// =============================================================================================
//
// -- THE DEFECT, MEASURED ---------------------------------------------------------------------
//
// The camera was the boat, by construction: `world-entry.ts` set `camPx` to the rounded boat
// position and `world-view.ts` drew the hull at the literal `ART_W / 2`. So the boat had exactly
// 270 art px of frame above it and 270 below, whatever it had sailed to.
//
// `tools/lanes/surfaces/occlusion.mjs`, run at ba17e51 over the twenty charted places with the
// boat moored at each: SEVEN islands lose pixels, and the worst loss is not a generated islet -
// it is MURRIN ROOST, the one hand-authored island, the one the owner ruled on. Its sprite box
// lands at screen y -359, so 359 of its 562 rows are off the TOP of the frame and 63.9% of the
// island the owner approved has never been on screen in the running game.
//
// It is arithmetic, not an accident of the interface. The berth puts the island's near edge
// `BERTH_CLEARANCE_LEAGUES` = 3 leagues = 103.8 px away and a generic body is 238 px tall:
// 341.8 px demanded against 270 available. Deleting every pixel of the chart moves the count
// from 7 to 6 and the worst case from 64.2% to 63.9% - the previous round measured that with
// `--strip 0 --open 0`. Nothing the interface does can reach it.
//
// -- WHY THE CAMERA, AND WHY IT LIVES IN THIS FILE --------------------------------------------
//
// CONTRACT 1.5n(a) made arrival a STATE - "the island fills the frame, the player is somewhere" -
// rather than a collision, so there is a state to hang a camera on, and the frame looking at the
// island rather than at the boat is what that ruling asks for in pixels.
//
// It is here because it must be PURE and it must be IMPORTABLE BY AN INSTRUMENT. `world-view.ts`
// pulls in PixiJS and `import.meta.glob`, which is why the renderer's fences read its source
// instead of importing it (see world-view.test.ts). This file imports one type and two pure
// functions from `water.ts` (the tick the pan is quantised to) and nothing else, so
// `occlusion.mjs` bundles the SHIPPED function and the reported number cannot drift from the
// picture. It is the same reason the smoother above is here rather than in the host.
//
// -- WHAT THE FIT WILL NOT DO -----------------------------------------------------------------
//
// The FIT never moves the camera ACROSS. Two islands are wider than the frame - Murrin Roost at
// 2,483 px and Torsk Vard at 1,756 px against 960 - and both anchors are authored, `authored.json`
// `anchorWhy`: "Open water off the depicted quay" and "Open water below the depicted L-shaped
// working quay". Centring either box across would swing the frame off the quay the drawing was
// anchored on. The horizontal composition was chosen; the vertical one was never chosen at all.
// Only the vertical is FITTED.
//
// What does move across is the PAN under "THE PAN" below, ruled one round later by CONTRACT
// 1.5q(b): a too-large island is panned along, not shrunk. It keeps the authored anchor as the
// frame it dwells at and returns to, and it exists only for a body wider than the frame.

/** A sprite's box in the space the shift is asked for: screen art px, down-screen positive. */
export interface LookBox {
  readonly y0: number;
  readonly y1: number;
}

/**
 * The room the arrival camera has, all in SCREEN art px, all measured by the host off things
 * that exist rather than chosen: the band is the frame minus the strip of paper the chart owns
 * when it is shut, and the keep-box is that band inset by the boat's own rotated half-extent
 * and a margin. Nothing here knows a number; `world-entry.ts` derives every field.
 */
export interface LookBudget {
  /** The band the island is fitted inside. */
  readonly bandTop: number;
  readonly bandBottom: number;
  /** Where the boat's centre sits when the camera is on it, which is `ART_H / 2`. */
  readonly restY: number;
  /** The boat's centre may not leave these, and that is what keeps the boat findable. */
  readonly boatTop: number;
  readonly boatBottom: number;
}

/**
 * A gap between the hull and the paper, so a moored boat is CLEAR of the chart rather than on it.
 * The one judgement in the arrival camera; every other figure is measured off an asset.
 */
export const BOAT_KEEP_MARGIN_PX = 12;

/**
 * THE BUDGET, from the three things it is actually made of. Exported rather than assembled in the
 * host because `tools/lanes/surfaces/occlusion.mjs` has to compose the same budget to report where
 * an island lands, and a budget stated twice is a number that drifts from its own picture.
 *
 * `artH` is the frame, `stripH` the paper the chart owns while it is shut, `boatHalfPx` half the
 * side of the square the hull is rotated into - `rotate.ts` `rotatedSide` of the BAKED sprite, so
 * a bigger boat narrows the swing by being baked rather than by being retyped here.
 */
export function lookBudgetFor(artH: number, stripH: number, boatHalfPx: number, marginPx = BOAT_KEEP_MARGIN_PX, restY = artH / 2): LookBudget {
  const bandBottom = artH - stripH;
  return {
    bandTop: 0,
    bandBottom,
    // CONTRACT 1.5ac: the view's height varies, and the renderer centres the camera on the whole
    // row `floor(h / 2)`. The host passes that row so the budget and the picture agree on an odd
    // view; the default is the authored frame's 270, which is the same number.
    restY,
    boatTop: boatHalfPx + marginPx,
    boatBottom: bandBottom - boatHalfPx - marginPx,
  };
}

/**
 * HOW MUCH OF THE ARRIVAL FRAME IS TAKEN, 0 to 1, AND WHERE THE NUMBER COMES FROM.
 *
 * It is the game's own `callHoldWeight` - 0 on the spiral, 1 made fast, a smoothstep over the
 * call's run-in - carried through the smoother as `Fix.hold` and multiplied by a scale the host
 * exposes as `?look`. There is NO radius and no falloff invented here, and the first version of
 * this camera had both: the note on `Fix.hold` records what that cost and how it was caught.
 *
 * Three properties come free from taking the sim's own ramp rather than a distance:
 *
 *   IT IS ZERO IN OPEN WATER BY CONSTRUCTION, not by a measured threshold. Outside a call
 *   `callHoldWeight` is 0, so a boat merely sailing past an island draws the camera this
 *   renderer had before any of this existed - the same pixels, not nearly the same.
 *   IT IS ONE WHEN THE GAME SAYS THE BOAT IS THERE, which is the state CONTRACT 1.5n(a) made.
 *   IT EASES OVER THE RUN-IN THE BOAT IS ALREADY EASING OVER, `CALL_APPROACH_LEAGUES`, so the
 *   frame and the hull are moving to the same curve instead of two curves that have to be
 *   tuned against each other.
 */
export function lookWeight(hold: number, scale = 1): number {
  const h = hold <= 0 ? 0 : hold >= 1 ? 1 : hold;
  const s = scale <= 0 ? 0 : scale >= 1 ? 1 : scale;
  return h * s;
}

/**
 * WHERE THE ISLAND WANTS THE FRAME, before the boat gets a say: the smallest down-screen shift
 * that brings the body inside the band, or - for a measured body taller than the band, as Murrin
 * Roost is - the shift that centres it and shares the loss.
 *
 * Positive is down-screen. The camera moves the opposite way, so a positive shift moves the boat
 * down the frame too: everything in the picture moves together, which is what a camera is.
 */
export function fitShiftPx(body: LookBox, b: LookBudget): number {
  const room = b.bandBottom - b.bandTop;
  const h = body.y1 - body.y0;
  if (h > room) return (b.bandTop + b.bandBottom) / 2 - (body.y0 + body.y1) / 2;
  const lo = b.bandTop - body.y0;
  const hi = b.bandBottom - body.y1;
  return lo > 0 ? lo : hi < 0 ? hi : 0;
}

/**
 * THE SHIFT ACTUALLY TAKEN: what the island asked for, clamped to what leaves the boat whole and
 * on the water, then eased in by the arrival weight.
 *
 * The clamp is the reason this cannot fix Murrin Roost outright, and it is not a compromise that
 * can be tuned away. Its baked body is taller and wider than the 960 x 540 frame, so
 * no camera makes it whole; what a camera can do is stop the frame pointing at the water below it.
 */
export function lookShiftPx(body: LookBox, b: LookBudget, weight: number): number {
  if (!(weight > 0)) return 0;
  const want = fitShiftPx(body, b);
  const clamped = Math.min(b.boatBottom - b.restY, Math.max(b.boatTop - b.restY, want));
  return clamped * (weight >= 1 ? 1 : weight);
}

// =============================================================================================
// THE PAN: A TOO-LARGE ISLAND IS PANNED ALONG, NOT SHRUNK (CONTRACT 1.5q(b))
// =============================================================================================
//
// -- THE DEFECT, MEASURED ---------------------------------------------------------------------
//
// `tools/lanes/surfaces/occlusion.mjs` at the round-7 camera, on the baked islands this branch
// ships: Murrin Roost's box lands at screen x -347 to 2,136 with the boat moored, so 347 px of it
// are off the left and 1,176 off the right of a 960 px frame; Torsk Vard's lands at -985 to 771,
// 985 px off the left and none off the right. 61.3% of Murrin Roost's width and 56.1% of Torsk
// Vard's is never drawn while the boat is moored there, at ANY vertical camera, because the fit
// above moves only up and down. They are the two islands wider than the frame: the widest
// generated sprite in the baked manifest is 359 px. (The previous port of this pan was measured
// on a base where Torsk Vard still wore a 250 px placeholder; the figures here are this branch's.)
//
// -- THE SHAPE, and why each part is what it is --------------------------------------------------
//
//   IT PANS ONLY A BODY WIDER THAN THE FRAME. For every narrower body the offset is exactly 0 at
//   every instant, so eighteen of the twenty charted frames are the bytes they were - the same
//   control round 7 kept with Cloven Rigg. A narrow body off-centre may still hide pixels; it is
//   left to the authored composition, because a pan that starts on "any hidden pixel" moves the
//   camera for nearly every place.
//
//   THE BOAT STAYS IN FRAME, by the same rule the vertical fit keeps: the camera may go no
//   further than leaves the hull inside the frame by its rotated half-extent plus the 12 px gap.
//   `panBudgetFor` is `lookBudgetFor` turned on its side. Within that, a sweep never goes further
//   than the island has pixels on that side - a frame that ran past the island's edge would be
//   showing empty water for the sake of moving.
//
//   THE MOTION is a dwell at the authored anchor (offset 0), a constant-speed sweep toward the
//   side with more hidden island, a dwell at that end, a sweep back through the anchor to the
//   other end, a dwell, and a sweep home; then the cycle repeats. The anchor is where the frame
//   RESTS because `anchorWhy` chose it; the sweep is what shows the rest.
//
//   IT ADVANCES ONLY ON WATER-TICK BOUNDARIES. The offset is a function of the tick the water is
//   on, `waterTickAt`, and of nothing finer, so over one full cycle at a mooring the number of
//   FRAME-KEY CHANGES equals the number of water ticks. (Changes, not distinct strings: the water
//   frame is a 16-loop, so the same key recurs inside a dwell.) That is the invariant
//   `interpolate-pan.test.ts` fences, and `pan.mjs`'s photographer counts it on the SHIPPED host
//   rather than on the model: `pan.json` `browser.frameKeyChanges`, one page per dial over the
//   same span of one mooring at 2 ms resolution. It is why the host's sleep budget
//   (`nextDelayMs`) needs no term for the pan - the water tick is already its floor. 2 art px per
//   190 ms tick is 10.5 px/s; the boat's own screen travel at a mooring is 0.
//
//   SAID EXACTLY, BECAUSE THE WHOLE-HOST FIGURE IS NOT THE SAME FIGURE. The host paints from five
//   places, not one: the frame key, the chart key, the catch mark, the fishing visual, and the
//   fishing visual being cleared. Three of those are laid out from the BOAT'S SCREEN POSITION,
//   which the pan moves across the frame - so a catch ceremony re-lays itself as the sweep goes,
//   and the host's own draw counter is HIGHER with the pan on. Both numbers are in
//   `pan.json` and neither is allowed to stand for the other.
//
//   IT IS MULTIPLIED BY THE ARRIVAL WEIGHT, the same `lookWeight` as the vertical shift, so
//   leaving a mooring un-pans along the ramp the frame is already descending, and the host chords
//   it between the smoother's two endpoints exactly as it chords the vertical shift. The three
//   deaths of round 7 are answered by construction: it is not a chord of a step (the sweep is
//   continuous in the weight and moves 2 px a tick in time), it names no island by proximity
//   (the box comes from the call's own place), and it cannot outrun the swing budget (it exists
//   only at weight 1 while the boat is still; on the stand-out it shrinks with the same weight
//   the vertical does, so its worst px-per-league is `reach x 1.5 / CALL_APPROACH_LEAGUES`,
//   reported by `tools/lanes/surfaces/pan.mjs`).
//
//   THE BOX IS TAKEN AT THE MOORED BOAT, not at the drawn one. The sweep's geometry - how far
//   each way, and which way first - is a discrete choice, and a choice re-made from a box that
//   moves with the boat could flip mid-stand-out. Taken at the call's own point it is one fixed
//   shape per place and the only thing that varies with the boat is the weight.
//
// The water is re-measured under it by `pan.mjs` with `arrival.mjs`'s own method, because 1.5o
// records that a camera change moves which water is on screen and the sea was nearly un-settled
// once by a change that never mentioned it. The calm field's live disc stays on the BOAT.

/** A sprite's box across the screen, in screen art px with the camera on the moored boat. */
export interface PanBox {
  readonly x0: number;
  readonly x1: number;
}

/** GUESS, in the same spirit as the vertical gap: the frame rests on the authored anchor this long. */
export const PAN_DWELL_ANCHOR_MS = 8000;
/** GUESS: the frame rests at each end of a sweep this long. */
export const PAN_DWELL_END_MS = 5000;
/** GUESS: art px the camera moves per water tick. At WATER_FRAME_MS = 190 that is 10.5 px/s. */
export const PAN_PX_PER_TICK = 2;

/**
 * THE HORIZONTAL BUDGET: how far the camera may leave the boat across before the hull's rotated
 * half-extent plus the gap would touch a frame edge. `lookBudgetFor` on its side, with no strip
 * to subtract because the chart sits along the bottom, not the sides. Whole art px, never below 0.
 */
export function panBudgetFor(artW: number, boatHalfPx: number, marginPx = BOAT_KEEP_MARGIN_PX): number {
  return Math.max(0, Math.floor(artW / 2 - boatHalfPx - marginPx));
}

/** The sweep's fixed geometry for one body: how far each way, and which way first. */
export interface PanReach {
  /** Camera travel to the LEFT that shows more island, art px, 0..budget. */
  readonly left: number;
  /** Camera travel to the RIGHT, art px, 0..budget. */
  readonly right: number;
  /** Which end the first sweep goes to: the side with more hidden island. Ties go right. */
  readonly firstRight: boolean;
}

/**
 * How far the frame can usefully go each way: the hidden width on that side, capped by the
 * budget. Exactly 0 both ways for a body no wider than the frame, which is the whole control.
 */
export function panReachFor(body: PanBox, artW: number, budgetPx: number): PanReach {
  if (!(budgetPx > 0) || body.x1 - body.x0 <= artW) return { left: 0, right: 0, firstRight: true };
  const hiddenLeft = Math.max(0, -body.x0);
  const hiddenRight = Math.max(0, body.x1 - artW);
  return {
    left: Math.min(budgetPx, Math.round(hiddenLeft)),
    right: Math.min(budgetPx, Math.round(hiddenRight)),
    firstRight: hiddenRight >= hiddenLeft,
  };
}

/** One leg of the cycle: `ticks` long, from one offset to another (equal for a dwell). */
interface PanLeg {
  readonly ticks: number;
  readonly from: number;
  readonly to: number;
}

/** The whole cycle as legs. A reach of 0 on a side drops that side's dwell and its leg. */
function panLegs(reach: PanReach, frameMs: number): PanLeg[] {
  const dwellA = Math.max(1, Math.round(PAN_DWELL_ANCHOR_MS / frameMs));
  const dwellE = Math.max(1, Math.round(PAN_DWELL_END_MS / frameMs));
  const first = reach.firstRight ? reach.right : -reach.left;
  const second = reach.firstRight ? -reach.left : reach.right;
  const legs: PanLeg[] = [{ ticks: dwellA, from: 0, to: 0 }];
  const sweep = (from: number, to: number): void => {
    const d = Math.abs(to - from);
    if (d > 0) legs.push({ ticks: Math.ceil(d / PAN_PX_PER_TICK), from, to });
  };
  if (first !== 0) {
    sweep(0, first);
    legs.push({ ticks: dwellE, from: first, to: first });
  }
  if (second !== 0) {
    sweep(first, second);
    legs.push({ ticks: dwellE, from: second, to: second });
    sweep(second, 0);
  } else {
    sweep(first, 0);
  }
  return legs;
}

/** Water ticks in one full cycle for this reach; 1 when there is nothing to sweep. */
export function panCycleTicks(reach: PanReach, frameMs = WATER_FRAME_MS): number {
  if (reach.left === 0 && reach.right === 0) return 1;
  let n = 0;
  for (const leg of panLegs(reach, frameMs)) n += leg.ticks;
  return n;
}

/**
 * Whole water ticks since the mooring began, negative before it. THE ONLY CLOCK THE PAN READS:
 * `waterTickAt(nowMs)` first, so the count steps exactly when the water's own frame does and at
 * no other instant, whatever grid the mooring epoch fell on.
 */
export function panTicksSince(nowMs: number, moorEpochMs: number, frameMs = WATER_FRAME_MS): number {
  const f = Math.max(1, frameMs);
  return Math.floor((waterTickAt(nowMs, f) - moorEpochMs) / f);
}

/**
 * THE UNWEIGHTED SWEEP at a tick count: whole art px, positive when the camera has gone right
 * (showing the island's right side, the hull drawn left of centre). 0 before the mooring, 0 in
 * the anchor dwell, and never further than `reach` either way.
 */
export function panSweepPx(reach: PanReach, ticks: number, frameMs = WATER_FRAME_MS): number {
  if (reach.left === 0 && reach.right === 0) return 0;
  if (ticks < 0) return 0;
  const legs = panLegs(reach, frameMs);
  let k = ticks % panCycleTicks(reach, frameMs);
  for (const leg of legs) {
    if (k < leg.ticks) {
      if (leg.from === leg.to) return leg.from;
      const dir = leg.to > leg.from ? 1 : -1;
      return leg.from + dir * Math.min(Math.abs(leg.to - leg.from), PAN_PX_PER_TICK * k);
    }
    k -= leg.ticks;
  }
  return 0;
}

/**
 * THE OFFSET ACTUALLY TAKEN: the sweep for this body at this instant, eased by the arrival
 * weight. Unrounded, like `lookShiftPx`, so the host can chord two endpoints and round once;
 * at weight 1 it is a whole number of art px. `moorEpochMs` null is "no mooring", offset 0.
 */
export function panOffsetPx(
  body: PanBox,
  artW: number,
  budgetPx: number,
  weight: number,
  nowMs: number,
  moorEpochMs: number | null,
  frameMs = WATER_FRAME_MS,
): number {
  if (!(weight > 0) || moorEpochMs === null) return 0;
  const reach = panReachFor(body, artW, budgetPx);
  const sweep = panSweepPx(reach, panTicksSince(nowMs, moorEpochMs, frameMs), frameMs);
  return sweep * (weight >= 1 ? 1 : weight);
}
